<template>
  <div
    id="app"
    @click="clickHandler"
    :data-browser-name="detect.browser.name"
    :data-browser-version="detect.browser.version"
    :data-os-name="detect.os.name"
    :data-os-version="detect.os.version"
    :data-platform-model="detect.platform.model"
    :data-platform-type="detect.platform.type"
    :data-platform-vendor="detect.platform.vendor"
  >
    <Header :title="$route.meta.title" />

    <div
      class="page"
      :class="{
        'numeric-pad-open': numericPadStatus === 'open',
        'help-open': helpStatus === 'open',
        'global-loader-active': globalLoaderActive === true
      }"
    >
      <GlobalLoader />
      <Modal id="genericModal" :title="modalData.title" :size="modalData.size">
        <template v-slot:content>
          {{ modalData.message }}
          <div
            v-if="modalData.htmlMessage"
            v-html="modalData.htmlMessage"
          ></div>
          <div class="modal-trace" v-if="modalData.trace">
            Codice di errore: {{ modalData.trace }}
          </div>
        </template>
        <template v-slot:buttons>
          <button
            class="btn btn--outline"
            @click="
              () => {
                $root.$emit('closeModal', 'genericModal');
                if (modalData.undoEvent) {
                  $root.$emit(modalData.undoEvent);
                }
              }
            "
            type="button"
          >
            {{ modalData.undoText ? modalData.undoText : "Chiudi" }}
          </button>
          <button
            v-if="modalData.onConfirm"
            class="btn"
            @click="
              () => {
                modalData.onConfirm();
              }
            "
            type="button"
          >
            {{ modalData.confirmText ? modalData.confirmText : "Conferma" }}
          </button>
        </template>
      </Modal>
      <Modal id="modalBalanceError" title="Addebito non riuscito">
        <template v-slot:content>
          <img
            src="./assets/images/bloccoApp.png"
            style="max-width: 150px; margin: 0px auto 20px;"
          />
          <p class="mb-10">
            Non siamo riusciti ad addebitare la quota necessaria e quindi non è
            avvenuto il rilascio della tua nuova carta.
          </p>
        </template>

        <template v-slot:buttons>
          <button class="btn" @click="rechargeWallet" type="button">
            Ricarica ora il tuo conto
          </button>
        </template>
      </Modal>
      <div
        class="page-content"
        :class="
          $route.name === 'RegistrationCompleted' ||
          $route.name === 'Home' ||
          $route.name === 'MinorRegistrationCompleted'
            ? 'full-width'
            : ''
        "
      >
        <transition name="fade" mode="out-in">
          <router-view class="view" />
        </transition>
      </div>
      <Footer />
      <Help />
    </div>
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import GlobalLoader from "@/components/GlobalLoader.vue";
import Modal from "@/components/Modal.vue";
import Help from "@/views/help/Help.vue";
import AppFlow from "@/AppFlow.js";
import Bowser from "bowser";
import ApiService from "@/services/ApiService";
import RegistrationService from "@/services/RegistrationService";
import LoginService from "@/services/LoginService";

import UtilityService from "@/services/UtilityService";
import BootstrapService from "@/services/BootstrapService";
import store from "@/store";

export default {
  name: "App",
  components: {
    Header,
    Footer,
    GlobalLoader,
    Modal,
    Help
  },
  data() {
    return {
      modalData: {},
      numericPadStatus: "closed",
      helpStatus: "closed",
      globalLoaderActive: false,
      modalOpen: false,
      detect: {
        browser: {},
        os: {},
        platform: {}
      }
    };
  },
  created() {
    window.vm = this;

    //TODO: maybe not needed
    if (process && process.env && process.env.NODE_ENV !== "development") {
      window.console.log = function() {};
    }
  },
  async mounted() {
    console.log("APP MOUNTED");
    this.retrieveTokenFromUrl();
    this.detectBrowser();
    this.handleStepChange();
    this.addEventHandlers();

    const token = sessionStorage.getItem("cr_at");

    if (!token) {
      //users must be logged to continue
      return;
    }

    const isUpgradePhysical = sessionStorage.getItem("cr_upgrade_physical");
    if (isUpgradePhysical) {
      AppFlow.setStepsFlow("UPGRADE_PHYSICAL");
    }

    LoginService.setAccessToken(sessionStorage.getItem("cr_at"));
    LoginService.setRefreshToken(sessionStorage.getItem("cr_rt"));
    store.commit("setLoggedIn", true);
    const results = await BootstrapService.init();
    console.log(results);
    const currentRegistration = await RegistrationService.getMyRegistrations();
    if (!currentRegistration) {
      this.goToLandingPage();
    } else {
      //IF registrations found:
      let currentStep = currentRegistration.currentStep;

      //This is a patch for zero based indexes from BE
      if (currentStep === 0) currentStep = 1;

      //When user interrupt while infocert sign is running this will recover that signature
      
      const infocertSignRunning =
        currentRegistration.infocertSignRunning === true;
      if (infocertSignRunning) {
        currentRegistration.flow === "PREPAID_CARD"
          ? this.goToRecoverSignature()
          : this.goToRecoverSignatureTutor();
        return;
      }

      //PATCH: For users that got stuck in an inconsistent signature step
      const userSignedWithErrors =
        currentRegistration.flow === "PREPAID_CARD" &&
        currentRegistration.status === "CONTRACTS_SIGNED" &&
        (currentStep === 4 || currentStep === 5);
      if (userSignedWithErrors) {
        RegistrationService.upgradeRegistrationProgress(5).then(() => {
          this.goToFinalStep();
          return;
        });
        return;
      }

      //PATCH: For minor users that got stuck in an inconsistent signature step
      const minorSignedWithErrors =
        currentRegistration.flow === "PREPAID_CARD_MINOR" &&
        currentRegistration.status === "CONTRACTS_SIGNED" &&
        (currentStep === 5 || currentStep === 6);
      if (minorSignedWithErrors) {
        RegistrationService.upgradeRegistrationProgress(5).then(() => {
          this.goToFinalStep(7);
          return;
        });
        return;
      }
      

      if (currentRegistration.flow === "PREPAID_CARD" && currentStep === 4) {
        //This is a patch: recovering step 4 isn't safe, because infocert certificate could be expired
        //so we force to restart from step 3
        currentStep = 3;
      }

      this.$store.dispatch("setCurrentStep", currentStep);
      this.$store.dispatch("setAppReady", true);
    }
  },
  methods: {
    goToLandingPage() {
      this.$store.dispatch("setCurrentStep", 0);
      this.$store.dispatch("setAppReady", true);
    },
    goToRecoverSignature() {
      this.$store.dispatch("setCurrentStep", 4);
      this.$store.dispatch("setCurrentSubstep", 3);
      this.$store.dispatch("setAppReady", true);
    },
    goToRecoverSignatureTutor() {
      this.$store.dispatch("setCurrentStep", 5);
      this.$store.dispatch("setCurrentSubstep", 6);
      this.$store.dispatch("setAppReady", true);
    },
    goToFinalStep(step = 6) {
      this.$store.dispatch("setCurrentStep", step);
      this.$store.dispatch("setAppReady", true);
    },
    retrieveTokenFromUrl() {
      console.log("Retrieving info from URL...", this.$route.query);

      if (this.$route.query && this.$route.query.fromApp) {
        console.log("[Info] User comes from App!");
        LoginService.setFromApp();
      }

      if (this.$route.query && this.$route.query.isUpgradePhysical) {
        console.log("[Info] Is upgrade to physical card!");
        sessionStorage.setItem("cr_upgrade_physical", true);
        AppFlow.setStepsFlow("UPGRADE_PHYSICAL");
      }

      if (this.$route.query && this.$route.query.idRegistration) {
        console.log("[Info] User is tutor!");
        console.log(
          "[Info] Setting current registration",
          this.$route.query.idRegistration
        );
        RegistrationService.setCurrentRegistrationId(
          this.$route.query.idRegistration
        );
        RegistrationService.setIsTutor(true);
      }

      if (this.$route.query && this.$route.query.cookieConsents) {
        console.log(
          "[Info] Setting cookie consents ",
          this.$route.query.cookieConsents
        );
        sessionStorage.setItem(
          "cookieConsents",
          this.$route.query.cookieConsents
        );
      }

      if (
        this.$route.query &&
        this.$route.query.access_token &&
        this.$route.query.refresh_token
      ) {
        console.log("[Info] User has a token!");
        sessionStorage.setItem("cr_at", this.$route.query.access_token);
        sessionStorage.setItem("cr_rt", this.$route.query.refresh_token);

        ApiService.setToken(this.$route.query.access_token);

        //remove from url
        let query = Object.assign({}, this.$route.query);
        delete query.access_token;
        delete query.refresh_token;
        delete query.fromApp;
        delete query.idRegistration;
        delete query.isUpgradePhyiscal;
        console.log("Clean up URL from data");
        this.$router.replace({ query });
      }
    },
    detectBrowser() {
      const browser = Bowser.getParser(window.navigator.userAgent);
      this.$store.dispatch("setDetect", browser.parsedResult);
      this.detect = browser.parsedResult;

      if (this.detectLargeText()) {
        document.getElementById("app").classList.add("large-text-mode");
      }
    },
     detectLargeText() {
      try {
        const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const dpr = window.devicePixelRatio || 1;

        return fontSize > 18 || dpr < 1;
      } catch (error) {
        return false;
      }
    },

    clickHandler(event) {
      if (this.numericPadStatus !== "closed") {
        const clickedNumericPad = event.target.closest(".numeric-pad") !== null;
        if (!clickedNumericPad) this.$root.$emit("closeNumericPad");
      }

      if (this.helpStatus !== "closed") {
        const clickedBox = event.target.closest(".help") !== null;
        const clickedHelp = event.target.closest(".js-open-help") !== null;
        if (!clickedHelp && !clickedBox) this.$root.$emit("closeHelp");
      }

      //This is a patch for mobile only, to avoid focusing input when tapping into the label
      //TODO: instead we should replace all <label> occurences with any other tag
      if (
        event.target.closest("[data-platform-type='mobile']") &&
        event.target.closest(".tnb-input") &&
        event.target.nodeName === "LABEL"
      ) {
        event.preventDefault();
      }
    },
    addEventHandlers() {
      this.$root.$on("openNumericPad", () => {
        this.numericPadStatus = "open";
      });
      this.$root.$on("closeNumericPad", () => {
        this.numericPadStatus = "closed";
      });
      this.$root.$on("openHelp", () => {
        this.helpStatus = "open";
      });
      this.$root.$on("closeHelp", () => {
        this.helpStatus = "closed";
      });
      this.$root.$on("openGlobalLoader", () => {
        this.globalLoaderActive = true;
      });
      this.$root.$on("closeGlobalLoader", () => {
        this.globalLoaderActive = false;
      });
      this.$root.$on("sendPostMessage", message => {
        UtilityService.sendPostMessage(message);
      });

      this.$root.$on(`openModal`, (modalName, data) => {
        document.querySelector("html").classList.add("modal-open");
        this.$root.$emit(`openModal@${modalName}`, data);
      });
      this.$root.$on(`closeModal`, modalName => {
        document.querySelector("html").classList.remove("modal-open");
        this.$root.$emit(`closeModal@${modalName}`);
      });

      this.$root.$on("confirmInterruptRegistration", () => {
        this.$root.$emit("openModal", "genericModal", {
          title: "Attenzione",
          message:
            "Proseguendo interromperai la richiesta carta. Potrai comunque riprenderla in un momento successivo.",
          undoText: "Continua la registrazione",
          confirmText: "Esci dalla registrazione",
          onConfirm: () => {
            UtilityService.backToInitialView();
          }
        });
      });

      this.$root.$on("openModal", (id, data) => {
        if (id === "genericModal") this.modalData = data;
      });
      this.$root.$on("submitStep", () => {
        const totalSubsteps = AppFlow.getCurrentTotalSubsteps();
        const currentSubstep = AppFlow.getCurrentSubstepIndex(
          this.$router.currentRoute.name
        );
        const isLastStep = currentSubstep === totalSubsteps;
        console.log("submitting step ", currentSubstep, "of", totalSubsteps);

        if (isLastStep) {
          //go to next STEP
          this.$store.dispatch(
            "setCurrentStep",
            this.$store.state.currentStep + 1
          );
        } else {
          //go to next SUBSTEP
          this.$store.dispatch("setCurrentSubstep", currentSubstep + 1);
        }
      });

      this.$root.$on("viewLoaded", this.eventTrackingHandler);
      this.$root.$on("appMessage", this.appMessageHandler);

      this.$root.$on(`storeUpdated@setAppReady`, state => {
        if (state.appReady) {
          UtilityService.sendPostMessage({
            action: "appIsReady"
          });
        }
      });

      window.addEventListener("popstate", () => {
        this.$root.$emit("popstate");
      });

      window.addEventListener("CookiebotOnDialogInit", function() {
        const userComesFromApp = sessionStorage.getItem("fromApp");
        const cookieConsents = sessionStorage.getItem("cookieConsents");
        const cookieBotActive = !!window.Cookiebot;

        //if user denied permission in app we hide the cookiebot
        if (userComesFromApp && cookieBotActive && cookieConsents == "false") {
          window.Cookiebot.submitCustomConsent(false, false, false);
          window.Cookiebot.hide();
        }
      });

    },
    handleStepChange() {
      this.$store.subscribe((mutation, state) => {
        this.$root.$emit(`storeUpdated@${mutation.type}`, state);
        if (mutation.type === "setCurrentStep") {
          //If we are going to init a main step, we also set the first substep
          this.$store.dispatch("setCurrentSubstep", 0);
        }

        if (mutation.type === "setCurrentSubstep") {
          const route = AppFlow.getRoute(
            state.currentStep,
            state.currentSubstep
          );
          if (route) {
            //avoid duplicate redirect to same page
            if (route.path !== this.$router.currentRoute.path) {
              this.$router.push({
                name: route.name,
                params: { SERVICE: true }
              });

              this.$root.$emit("changedRoute", route);
            }
          } else {
            this.$root.$emit("openModal", "genericModal", {
              title: "Ops!",
              message: "La pagina che stai visitando non esiste"
            });
          }
        }
      });
    },
    appMessageHandler(message) {
      console.log("************* MESSAGE FROM APP *************");
      console.log(message);
    },
    eventTrackingHandler(currentRoute) {
      UtilityService.EventTrackingHandler(currentRoute);
    },

    rechargeWallet() {
      UtilityService.goToWalletRecharge();
    }
  }
};
</script>

<style lang="less">
@import "../src/less/main.less";
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.1s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
